import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Eye, Loader2 } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { ToolListDialog } from "~/pages/manifold-nextgen/components/agents/ToolListDialog";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentConnector,
  AgentScope,
  Connector,
  agentConnectorsPath,
  connectorsPath,
  connectorToolsPath,
  disableAgentConnector,
  enableAgentConnector,
} from "~/services/agents";

interface AgentConnectorsTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // Forces the tab into a non-editable state regardless of the caller's
  // actual edit grant — used when embedding this tab in the project context
  // sidebar, which only ever shows a read-only view.
  readOnly?: boolean;
}

export const AgentConnectorsTab = ({
  agent: agentProp,
  readOnly = false,
}: AgentConnectorsTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const canEdit = !readOnly && canEditGrant;
  const { showError } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
  const [viewingConnector, setViewingConnector] = useState<Connector | null>(null);

  const { data: connectors, isLoading: connectorsLoading } =
    useFetcher<Connector[]>(connectorsPath());
  const { data: agentConnectors, isLoading: agentConnectorsLoading } = useFetcher<AgentConnector[]>(
    agentConnectorsPath(agent.id)
  );

  const enabledIds = new Set((agentConnectors ?? []).map((c) => c.connector.id));
  const isLoading = connectorsLoading || agentConnectorsLoading;
  const totalCount = connectors?.length ?? 0;

  const handleToggle = async (connector: Connector, enabled: boolean) => {
    const label = connector.display_name ?? connector.name;
    setTogglingIds((prev) => new Set(prev).add(connector.id));
    try {
      if (enabled) {
        await enableAgentConnector(agent.id, connector.id);
      } else {
        await disableAgentConnector(agent.id, connector.id);
      }
      await mutate(agentConnectorsPath(agent.id));
    } catch {
      showError(`Failed to ${enabled ? "enable" : "disable"} "${label}" Connector`);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(connector.id);
        return next;
      });
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <TabHeader
        title="Connectors"
        isSystemAgent={agent.scope === AgentScope.SYSTEM}
        count={isLoading || totalCount === 0 ? undefined : enabledIds.size}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        context={isLoading ? undefined : "External MCP servers the agent can reach"}
        description="Connectors expose tools hosted on external MCP servers. Attach the ones this agent needs to do its job."
      />

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : !connectors?.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No connectors available</p>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {connectors.map((connector) => {
            const label = connector.display_name ?? connector.name;
            return (
              <div key={connector.id} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{label}</p>
                  {connector.description && (
                    <p className="text-xs text-muted-foreground">{connector.description}</p>
                  )}
                  <p className="truncate text-xs text-muted-foreground/80" title={connector.url}>
                    {connector.url}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setViewingConnector(connector)}
                    aria-label={`View tools in ${label}`}
                    title="View tools"
                    className="size-8 text-muted-foreground"
                  >
                    <Eye className="size-4" />
                  </Button>
                  {togglingIds.has(connector.id) ? (
                    <div className="flex h-[1.15rem] w-8 items-center justify-center">
                      <Loader2 className="size-3 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <Switch
                      checked={enabledIds.has(connector.id)}
                      onCheckedChange={(checked) => handleToggle(connector, checked)}
                      disabled={!canEdit}
                      aria-label={`${enabledIds.has(connector.id) ? "Disable" : "Enable"} ${label}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ToolListDialog
        open={!!viewingConnector}
        onOpenChange={(open) => !open && setViewingConnector(null)}
        title={viewingConnector?.display_name ?? viewingConnector?.name ?? ""}
        description={viewingConnector?.description}
        fetchPath={viewingConnector ? connectorToolsPath(viewingConnector.id) : null}
      />
    </div>
  );
};
